import { cn } from '../../lib/utils';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

interface CoverageRatioDisplayProps {
  ratio: number;
  threshold?: number;
  projectedRatio?: number;
  showThreshold?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function getRatioColor(ratio: number, threshold: number = 100): string {
  if (ratio >= threshold) return 'text-green-600';
  if (ratio >= threshold * 0.9) return 'text-yellow-600';
  return 'text-red-600';
}

function getRatioBgColor(ratio: number, threshold: number = 100): string {
  if (ratio >= threshold) return 'bg-green-100';
  if (ratio >= threshold * 0.9) return 'bg-yellow-100';
  return 'bg-red-100';
}

function getProgressColor(ratio: number, threshold: number = 100): string {
  if (ratio >= threshold) return 'bg-green-500';
  if (ratio >= threshold * 0.9) return 'bg-yellow-500';
  return 'bg-red-500';
}

export function CoverageRatioDisplay({
  ratio,
  threshold = 100,
  projectedRatio,
  showThreshold = true,
  size = 'md',
  className,
}: CoverageRatioDisplayProps) {
  const displayRatio = Math.min(ratio, 200); // Cap at 200% for display
  const progressWidth = Math.min((displayRatio / 150) * 100, 100); // Scale to 150% max

  const textSize = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-2xl' : 'text-lg';
  const barHeight = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';

  const trend = projectedRatio !== undefined
    ? projectedRatio > ratio ? 'up' : projectedRatio < ratio ? 'down' : 'stable'
    : null;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn(textSize, 'font-semibold', getRatioColor(ratio, threshold))}>
            {ratio.toFixed(2)}%
          </span>
          {trend && (
            <span className={cn(
              'flex items-center gap-0.5 text-xs',
              trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'
            )}>
              {trend === 'up' && <TrendingUp className="h-3 w-3" />}
              {trend === 'down' && <TrendingDown className="h-3 w-3" />}
              {trend === 'stable' && <Minus className="h-3 w-3" />}
              {projectedRatio !== undefined && (
                <span>→ {projectedRatio.toFixed(2)}%</span>
              )}
            </span>
          )}
        </div>
        {showThreshold && (
          <span className="text-xs text-muted-foreground">
            Mínimo: {threshold}%
          </span>
        )}
      </div>

      <div className={cn('w-full rounded-full bg-gray-200', barHeight)}>
        <div
          className={cn('rounded-full transition-all duration-300', barHeight, getProgressColor(ratio, threshold))}
          style={{ width: `${progressWidth}%` }}
        />
      </div>

      {ratio < threshold && (
        <p className="text-xs text-red-600">
          ⚠️ Abaixo do limite mínimo de cobertura
        </p>
      )}
    </div>
  );
}

export function CoverageRatioGauge({
  ratio,
  threshold = 100,
  size = 120,
  className,
}: {
  ratio: number;
  threshold?: number;
  size?: number;
  className?: string;
}) {
  const normalizedRatio = Math.min(ratio, 200);
  const angle = (normalizedRatio / 200) * 180 - 90; // -90 to 90 degrees
  const thresholdAngle = (threshold / 200) * 180 - 90;

  const color = getRatioColor(ratio, threshold);
  const bgColor = getRatioBgColor(ratio, threshold);

  return (
    <div className={cn('relative inline-flex flex-col items-center', className)}>
      <svg width={size} height={size / 2 + 20} viewBox="0 0 120 80">
        {/* Background arc */}
        <path
          d="M 10 70 A 50 50 0 0 1 110 70"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
          strokeLinecap="round"
        />
        
        {/* Threshold marker */}
        <line
          x1="60"
          y1="70"
          x2={60 + 45 * Math.cos((thresholdAngle * Math.PI) / 180)}
          y2={70 - 45 * Math.sin((thresholdAngle * Math.PI) / 180)}
          stroke="#f59e0b"
          strokeWidth="2"
          strokeDasharray="4 2"
        />

        {/* Value arc */}
        <path
          d="M 10 70 A 50 50 0 0 1 110 70"
          fill="none"
          stroke={ratio >= threshold ? '#22c55e' : ratio >= threshold * 0.9 ? '#eab308' : '#ef4444'}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${(normalizedRatio / 200) * 157} 157`}
        />

        {/* Needle */}
        <line
          x1="60"
          y1="70"
          x2={60 + 40 * Math.cos((angle * Math.PI) / 180)}
          y2={70 - 40 * Math.sin((angle * Math.PI) / 180)}
          stroke="#374151"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="60" cy="70" r="4" fill="#374151" />
      </svg>

      <div className={cn('text-center -mt-2', bgColor, 'px-3 py-1 rounded-full')}>
        <span className={cn('text-lg font-bold', color)}>
          {ratio.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export default CoverageRatioDisplay;
